import React, { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { Badge, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import useStudyYearSelector from '../discipline/useStudyYearSelector';
import useNfcReader, { isValidUid, nfcUnavailableReason } from '../utils/useNfcReader';

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api')
  .replace(/\/api\/?$/, '').replace(/\/$/, '');
const photoUrl = (photo) => (photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null);

const thaiDate = (v) => (v ? new Date(v).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' }) : '-');

const STATUS_LABEL = {
  active: { text: 'ใช้งานอยู่', variant: 'success' },
  revoked: { text: 'ยกเลิกแล้ว', variant: 'secondary' },
  lost: { text: 'แจ้งหาย', variant: 'danger' }
};

const Avatar = ({ photo, name, size = 44 }) => {
  const src = photoUrl(photo);
  return src ? (
    <img src={src} alt={name} width={size} height={size}
      className="rounded-circle border bg-white flex-shrink-0" style={{ objectFit: 'cover' }} />
  ) : (
    <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center flex-shrink-0 fw-semibold"
      style={{ width: size, height: size, fontSize: size / 2.4 }} aria-hidden="true">
      {name?.trim().charAt(0) || '?'}
    </div>
  );
};

export default function AdminRfidCards() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearId } = useStudyYearSelector();

  const [tab, setTab] = useState('bind');

  // ขั้นที่ 1 — บัตร
  const [uid, setUid] = useState('');
  const [uidHolder, setUidHolder] = useState(null);
  const [checkingUid, setCheckingUid] = useState(false);
  const [manualUid, setManualUid] = useState(false);

  // ขั้นที่ 2 — เจ้าของ
  const [ownerType, setOwnerType] = useState('student');
  const [keyword, setKeyword] = useState('');
  const [people, setPeople] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);       // แถบยืนยันหลังผูกสำเร็จ

  // แท็บรายการบัตร
  const [cards, setCards] = useState([]);
  const [listKeyword, setListKeyword] = useState('');
  const [listStatus, setListStatus] = useState('active');
  const [loadingList, setLoadingList] = useState(false);

  const nfcBlocked = nfcUnavailableReason();
  const doneTimer = useRef(null);
  useEffect(() => () => clearTimeout(doneTimer.current), []);

  const checkUid = useCallback((value) => {
    setUidHolder(null);
    if (!isValidUid(value)) return;
    setCheckingUid(true);
    axiosInstance.get(`/api/admin/rfid-cards/lookup?uid=${value}`)
      .then(res => setUidHolder(res.data?.card || null))
      .catch(() => {})
      .finally(() => setCheckingUid(false));
  }, []);

  const handleScan = useCallback((scanned, { valid }) => {
    setDone(null);
    if (!valid) {
      Swal.fire({ icon: 'warning', title: 'UID ไม่ถูกต้อง', text: `อ่านได้ "${scanned}" ซึ่งผิดรูปแบบ ลองแตะใหม่` });
      return;
    }
    setUid(scanned);
    checkUid(scanned);
  }, [checkUid]);

  const { scanning, error: nfcError, start: startNfc } = useNfcReader(handleScan);

  // ค้นระหว่างพิมพ์ — บนมือถือการกดปุ่มค้นทุกครั้งเสียเวลา
  useEffect(() => {
    const kw = keyword.trim();
    if (kw.length < 2) { setPeople([]); return; }

    const timer = setTimeout(() => {
      setSearching(true);
      const req = ownerType === 'teacher'
        ? axiosInstance.get(`/api/admin/rfid-cards/teachers?keyword=${encodeURIComponent(kw)}`)
        : axiosInstance.get(`/api/discipline/conduct/students?keyword=${encodeURIComponent(kw)}&yearId=${yearId}`);
      req.then(res => setPeople((res.data || []).slice(0, 25)))
        .catch(() => setPeople([]))
        .finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [keyword, ownerType, yearId]);

  const fetchCards = useCallback(() => {
    setLoadingList(true);
    const params = new URLSearchParams({ keyword: listKeyword, status: listStatus });
    axiosInstance.get(`/api/admin/rfid-cards?${params}`)
      .then(res => setCards(res.data || []))
      .catch(() => Swal.fire({ icon: 'error', title: 'โหลดรายการบัตรไม่สำเร็จ' }))
      .finally(() => setLoadingList(false));
  }, [listKeyword, listStatus]);

  useEffect(() => { if (tab === 'list') fetchCards(); }, [tab, fetchCards]);

  const resetForm = () => {
    setUid(''); setUidHolder(null); setSelected(null);
    setPeople([]); setKeyword(''); setManualUid(false);
  };

  const handleBind = () => {
    setSaving(true);
    axiosInstance.post('/api/admin/rfid-cards', {
      uid,
      ownerType,
      ownerId: ownerType === 'teacher' ? selected.t_id : selected.st_id
    })
      .then(() => {
        // แถบยืนยันแทน popup เพื่อให้แตะใบถัดไปต่อได้ทันทีโดยไม่ต้องกดปิด
        setDone({ name: ownerType === 'teacher' ? selected.t_name : selected.st_name, uid });
        clearTimeout(doneTimer.current);
        doneTimer.current = setTimeout(() => setDone(null), 6000);
        resetForm();
      })
      .catch(err => Swal.fire({
        icon: 'error', title: 'ผูกบัตรไม่สำเร็จ',
        text: err.response?.data?.message || 'กรุณาลองใหม่'
      }))
      .finally(() => setSaving(false));
  };

  const handleRevoke = (card, status) => {
    Swal.fire({
      icon: 'warning',
      title: status === 'lost' ? 'แจ้งบัตรหาย?' : 'ยกเลิกบัตรใบนี้?',
      html: `<b>${card.owner_name || '-'}</b><br>UID ${card.card_uid}`,
      showCancelButton: true,
      confirmButtonText: status === 'lost' ? 'แจ้งหาย' : 'ยกเลิกบัตร',
      cancelButtonText: 'ไม่', confirmButtonColor: '#dc3545'
    }).then(r => {
      if (!r.isConfirmed) return;
      axiosInstance.put(`/api/admin/rfid-cards/${card.card_id}/revoke`, { status })
        .then(fetchCards)
        .catch(err => Swal.fire({ icon: 'error', title: 'ไม่สำเร็จ', text: err.response?.data?.message || '' }));
    });
  };

  const name = (p) => (ownerType === 'teacher' ? p.t_name : p.st_name);
  const code = (p) => (ownerType === 'teacher' ? p.t_idnumber : p.st_idnumber);
  const photo = (p) => (ownerType === 'teacher' ? p.t_photo : p.st_photo);
  const key = (p) => (ownerType === 'teacher' ? p.t_id : p.st_id);

  const cardReady = isValidUid(uid) && !uidHolder && !checkingUid;
  const canBind = cardReady && selected && !saving;

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div style={{ maxWidth: 620, margin: '0 auto' }}>

          <h4 className="main-title mb-2">ผูกบัตร RFID</h4>

          <div className="btn-group w-100 mb-3">
            {[['bind', 'ผูกบัตร'], ['list', 'รายการบัตร']].map(([k, label]) => (
              <Button key={k} variant={tab === k ? 'primary' : 'outline-primary'}
                onClick={() => setTab(k)}>{label}</Button>
            ))}
          </div>

          {tab === 'bind' && (
            <>
              {done && (
                <div className="alert alert-success d-flex align-items-center gap-2 py-2">
                  <i className="ri-checkbox-circle-fill fs-4" />
                  <div className="flex-grow-1">
                    <div className="fw-semibold">ผูกบัตรให้ {done.name} แล้ว</div>
                    <div className="fs-xs font-monospace">{done.uid} · แตะใบถัดไปได้เลย</div>
                  </div>
                </div>
              )}

              {/* ---------- ขั้นที่ 1 : บัตร ---------- */}
              <div className="card card-one mb-3">
                <div className="card-body p-3">
                  <div className="fs-sm text-secondary mb-2">1 · บัตร</div>

                  {!uid ? (
                    <>
                      {nfcBlocked ? (
                        <div className="alert alert-warning py-2 fs-sm mb-2">
                          {nfcBlocked} — พิมพ์ UID เองด้านล่าง
                        </div>
                      ) : (
                        <Button
                          variant={scanning ? 'success' : 'primary'}
                          className="w-100 py-4 fw-bold" style={{ fontSize: 18 }}
                          disabled={scanning} onClick={startNfc}
                        >
                          <i className={`ri-${scanning ? 'wifi' : 'cellphone'}-line d-block mb-1`} style={{ fontSize: 32 }} />
                          {scanning ? 'แตะบัตรที่ด้านหลังมือถือ' : 'เริ่มสแกนบัตร'}
                        </Button>
                      )}

                      {nfcError && <div className="alert alert-danger py-2 fs-sm mt-2 mb-0">{nfcError}</div>}

                      {!manualUid ? (
                        <button type="button" className="btn btn-link btn-sm w-100 mt-1 text-secondary"
                          onClick={() => setManualUid(true)}>พิมพ์ UID เอง</button>
                      ) : (
                        <Form.Control
                          autoFocus className="font-monospace fs-5 fw-bold text-center mt-2"
                          placeholder="A0097DA2"
                          onChange={(e) => {
                            const v = e.target.value.toUpperCase().replace(/[^0-9A-F]/g, '');
                            setUid(v); checkUid(v);
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <div className="d-flex align-items-center gap-2">
                        <div className="font-monospace fw-bold flex-grow-1" style={{ fontSize: 26, letterSpacing: 1 }}>
                          {uid}
                        </div>
                        <Button variant="outline-secondary" size="sm" onClick={resetForm}>
                          <i className="ri-refresh-line me-1" />เปลี่ยน
                        </Button>
                      </div>

                      <div className="fs-sm mt-2">
                        {checkingUid && <span className="text-secondary"><Spinner size="sm" animation="border" className="me-1" />กำลังตรวจ…</span>}
                        {!checkingUid && !isValidUid(uid) && (
                          <span className="text-warning">UID ต้องยาว 8, 14 หรือ 20 ตัว (ตอนนี้ {uid.length})</span>
                        )}
                        {!checkingUid && isValidUid(uid) && uidHolder && (
                          <span className="text-danger">
                            <i className="ri-error-warning-line me-1" />
                            บัตรนี้เป็นของ <b>{uidHolder.owner_name}</b> แล้ว — ยกเลิกใบเดิมก่อน
                          </span>
                        )}
                        {cardReady && <span className="text-success"><i className="ri-checkbox-circle-line me-1" />บัตรว่าง ผูกได้</span>}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* ---------- ขั้นที่ 2 : เจ้าของ ---------- */}
              {cardReady && (
                <div className="card card-one mb-3">
                  <div className="card-body p-3">
                    <div className="fs-sm text-secondary mb-2">2 · เจ้าของบัตร</div>

                    {selected ? (
                      <div className="d-flex align-items-center gap-2 p-2 border rounded">
                        <Avatar photo={photo(selected)} name={name(selected)} />
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{name(selected)}</div>
                          <div className="fs-xs text-secondary">{code(selected)}</div>
                        </div>
                        <Button size="sm" variant="outline-secondary" onClick={() => setSelected(null)}>เปลี่ยน</Button>
                      </div>
                    ) : (
                      <>
                        <div className="btn-group w-100 mb-2">
                          {[['student', 'นักเรียน'], ['teacher', 'ครู/เจ้าหน้าที่']].map(([k, label]) => (
                            <Button key={k} size="sm" variant={ownerType === k ? 'secondary' : 'outline-secondary'}
                              onClick={() => { setOwnerType(k); setPeople([]); setKeyword(''); }}>{label}</Button>
                          ))}
                        </div>

                        <InputGroup>
                          <Form.Control
                            value={keyword} autoComplete="off"
                            placeholder={ownerType === 'teacher' ? 'ชื่อครู หรือรหัส' : 'ชื่อนักเรียน หรือรหัส'}
                            onChange={(e) => setKeyword(e.target.value)}
                          />
                          <InputGroup.Text>
                            {searching ? <Spinner size="sm" animation="border" /> : <i className="ri-search-line" />}
                          </InputGroup.Text>
                        </InputGroup>

                        {keyword.trim().length > 0 && keyword.trim().length < 2 && (
                          <div className="fs-xs text-secondary mt-1">พิมพ์อย่างน้อย 2 ตัวอักษร</div>
                        )}
                        {keyword.trim().length >= 2 && !searching && people.length === 0 && (
                          <div className="fs-sm text-secondary text-center py-3">ไม่พบใครตรงกับคำค้นนี้</div>
                        )}

                        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                          {people.map(p => (
                            <button key={key(p)} type="button"
                              className="d-flex align-items-center gap-2 w-100 p-2 border-0 bg-transparent text-start border-bottom"
                              onClick={() => { setSelected(p); setPeople([]); setKeyword(''); }}>
                              <Avatar photo={photo(p)} name={name(p)} size={38} />
                              <div>
                                <div className="fs-sm">{name(p)}</div>
                                <div className="fs-xs text-secondary">
                                  {code(p)}{p.c_class ? ` · ${p.c_class}/${p.r_no}` : ''}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* ปุ่มยืนยันติดขอบล่าง — นิ้วโป้งเอื้อมถึงโดยไม่ต้องเลื่อนจอ */}
              {canBind && (
                <div className="position-sticky bottom-0 pb-3 pt-2"
                  style={{ background: 'linear-gradient(to top, var(--bs-body-bg) 70%, transparent)' }}>
                  <Button variant="success" className="w-100 py-3 fw-bold" style={{ fontSize: 19 }}
                    disabled={saving} onClick={handleBind}>
                    {saving ? <Spinner size="sm" animation="border" />
                      : <><i className="ri-links-line me-2" />ผูกบัตรให้ {name(selected)}</>}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* ---------- รายการบัตร ---------- */}
          {tab === 'list' && (
            <>
              <div className="d-flex gap-2 mb-3">
                <Form.Select style={{ width: 130 }} value={listStatus} onChange={(e) => setListStatus(e.target.value)}>
                  <option value="active">ใช้งานอยู่</option>
                  <option value="revoked">ยกเลิกแล้ว</option>
                  <option value="lost">แจ้งหาย</option>
                  <option value="all">ทั้งหมด</option>
                </Form.Select>
                <Form.Control placeholder="ค้นชื่อ / รหัส / UID"
                  value={listKeyword} onChange={(e) => setListKeyword(e.target.value)} />
              </div>

              {loadingList ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
              ) : cards.length === 0 ? (
                <div className="text-center text-secondary py-5">ยังไม่มีบัตรในหมวดนี้</div>
              ) : (
                cards.map(card => (
                  <div key={card.card_id} className="card card-one mb-2">
                    <div className="card-body p-2 d-flex align-items-center gap-2">
                      <Avatar photo={card.owner_photo} name={card.owner_name} size={40} />
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="fs-sm text-truncate">
                          {card.owner_name || <span className="text-danger">ไม่พบเจ้าของ</span>}
                          {card.owner_type === 'teacher' && <Badge bg="info" className="ms-1">เจ้าหน้าที่</Badge>}
                        </div>
                        <div className="fs-xs text-secondary">
                          <span className="font-monospace">{card.card_uid}</span> · {thaiDate(card.issued_at)}
                        </div>
                      </div>
                      {card.status === 'active' ? (
                        <div className="d-flex flex-column gap-1">
                          <Button size="sm" variant="outline-danger" className="py-0 px-2"
                            onClick={() => handleRevoke(card, 'lost')}>หาย</Button>
                          <Button size="sm" variant="outline-secondary" className="py-0 px-2"
                            onClick={() => handleRevoke(card, 'revoked')}>ยกเลิก</Button>
                        </div>
                      ) : (
                        <Badge bg={STATUS_LABEL[card.status]?.variant}>{STATUS_LABEL[card.status]?.text}</Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </>
          )}

        </div>
        <Footer />
      </div>
    </>
  );
}
